/**
 * The venue card and the programme screen.
 *
 * What the catalogue does not know is left out rather than invented: a row
 * whose only content would be "non noto" is simply not drawn. The buttons are
 * the exception — "Chiama" and "Indicazioni" stay on screen, disabled, so a
 * card always has the same shape and a button that cannot work looks like it.
 *
 * The corollary is that "we do not know" must not be said where we do. The
 * placing row used to answer "non confermato" for every venue that had not
 * won, which conflated two different things: a venue whose placing nobody
 * wrote down, and one we know perfectly well came second to somebody — the
 * winner of its own episode, sitting in the same catalogue.
 *
 * "Indicazioni" follows the same rule and is the sharpest case of it: handing
 * turn-by-turn directions to a pin that is only the middle of a street sends
 * somebody somewhere specific and wrong. It stays off until an address has
 * been confirmed.
 */

import {
  STATUS_LABELS,
  el,
  formatDate,
  formatEpisodeDate,
  outcomeLabel,
  allOf,
  zoneSlug,
} from './format.js';
import { icon } from './icons.js';
import { createCard } from './list-view.js';
import { venuePhoto } from './photo.js';
import {
  GUIDE_CREDIT,
  GUIDE_YEAR,
  byStars,
  guideCitySlug,
  guideCityTitle,
  isGuide,
  starsGlyph,
  starsWords,
  tvChef,
} from './guida.js';
import { programPill } from './programs.js';
import { venueEpisodeKey } from './puntate.js';

function keyValue(term, value) {
  const row = el('div', 'kv');
  row.append(el('dt', null, term));
  row.append(el('dd', null, value));
  return row;
}

function heroBlock(venue, { programNames, onOpenProgram, onShowOnMap }) {
  const hero = el('div', 'hero');
  hero.append(el('h1', null, venue.nome ?? venue.id));
  // Said before the zone, because someone arriving from the episode is looking
  // for the name they heard and needs to know at once that they are in the
  // right place under a different sign.
  if (venue.nome_in_onda && venue.nome_in_onda !== venue.nome) {
    hero.append(el('p', 'hero__former', `In onda come ${venue.nome_in_onda}`));
  }
  const place = el('div', 'hero__place');
  if (venue.zona) place.append(el('p', null, venue.zona));

  // On a phone the card covers the map, and someone who arrived from the list
  // or the search has no idea where the place is. The way there sits beside
  // the zone, which is where the eye already went to ask. Only with a pin to
  // show: a button that lands on an empty map would answer the question
  // wrongly. Beside the map, on a wide window, the map has already moved on
  // its own, and the stylesheet takes the button away.
  if (onShowOnMap && venue.lat !== null && venue.lng !== null) {
    const show = el('button', 'hero__map');
    show.type = 'button';
    show.append(icon('pin'), document.createTextNode('Vedi sulla mappa'));
    show.addEventListener('click', () => onShowOnMap(venue.id));
    place.append(show);
  }
  if (place.childElementCount) hero.append(place);

  const meta = el('div', 'card__meta');
  meta.append(
    programPill(venue.programma, programNames.get(venue.programma) ?? venue.programma, {
      onClick: () => onOpenProgram(venue.programma),
    })
  );

  const outcome = outcomeLabel(venue);
  if (outcome) meta.append(el('span', 'tag tag--win', outcome));
  if (isGuide(venue) && venue.stelle) {
    meta.append(el('span', 'tag tag--stars', starsGlyph(venue.stelle)));
  }
  meta.append(el('span', `state state--${venue.stato}`, STATUS_LABELS[venue.stato]));
  hero.append(meta);

  return hero;
}

/**
 * Whether we know where this place is well enough to send somebody there.
 *
 * Coordinates are not an address. Most of these pins are the middle of a
 * street, found by geocoding a name — good enough for a dot on a map, and not
 * good enough to hand to turn-by-turn navigation, which will drive someone to
 * a house number nobody checked. So the button needs a confirmed address, not
 * just a latitude.
 */
function canRoute(venue) {
  return venue.indirizzo_confermato === true && venue.lat !== null && venue.lng !== null;
}

/**
 * Returned as a fragment, not as a wrapping `div`, so that `.actions` ends up
 * a direct child of the scrolling card. A sticky element can only stick
 * inside its own parent's box: wrapped, the row would unstick a few pixels
 * later when the wrapper scrolled past, which is not sticking at all.
 */
/**
 * Puts text on the clipboard, by whichever of the two ways is available.
 *
 * The modern one refuses unless the document holds the focus and the page is
 * on a secure origin, and it refuses by throwing rather than by returning
 * false. The old selection-based one asks for neither, so it catches the
 * cases the new one drops. Saying nothing worked is the last resort, not the
 * first answer.
 */
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Falls through to the older way rather than giving up here.
  }

  try {
    const field = el('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.top = '0';
    field.style.opacity = '0';
    document.body.append(field);
    field.select();
    const done = document.execCommand('copy');
    field.remove();
    return done;
  } catch {
    return false;
  }
}

function actionsBlock(venue) {
  const wrapper = document.createDocumentFragment();
  const actions = el('div', 'actions');
  const routable = canRoute(venue);

  // A dead anchor looks like a working one until it is tapped, so an address we
  // cannot vouch for gets a disabled button instead.
  // Two rows of two: getting there and seeing what the place is like above,
  // calling and sharing below. Three on one row would squeeze every label.
  const directions = routable
    ? el('a', 'btn btn--primary')
    : el('button', 'btn');
  directions.append(icon('directions'), document.createTextNode('Indicazioni'));
  if (routable) {
    // The confirmed address, not the coordinates: it is the part a person
    // checked, and it is what a mapping app resolves best.
    const destination = encodeURIComponent(`${venue.indirizzo}, ${venue.zona ?? 'Italia'}`);
    directions.href = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    directions.target = '_blank';
    directions.rel = 'noopener noreferrer';
  } else {
    directions.type = 'button';
    directions.disabled = true;
  }
  actions.append(directions);

  /*
   * The venue on Google Maps: reviews, photos, opening hours — everything this
   * site deliberately does not keep. Separate from "Indicazioni" because it
   * answers a different question: what is it like, not how do I get there.
   *
   * Same rule as directions, for the same reason: without a confirmed address
   * a search can land on a namesake. With one, the listing the lookup matched
   * when we have it, and otherwise a search for the name at that address,
   * which finds the right place nearly always.
   */
  const maps = routable ? el('a', 'btn') : el('button', 'btn');
  maps.append(icon('map'), document.createTextNode('Google Maps'));
  if (routable) {
    const query = encodeURIComponent(`${venue.nome}, ${venue.indirizzo}`);
    const listing = venue.google_id
      ? `&query_place_id=${encodeURIComponent(venue.google_id)}`
      : '';
    maps.href = `https://www.google.com/maps/search/?api=1&query=${query}${listing}`;
    maps.target = '_blank';
    maps.rel = 'noopener noreferrer';
  } else {
    maps.type = 'button';
    maps.disabled = true;
  }
  actions.append(maps);

  // A number we have is a number worth offering. It is not held to the
  // address's standard on purpose: the worst a wrong one does is waste a call,
  // where a wrong route wastes an afternoon.
  const call = venue.telefono ? el('a', 'btn') : el('button', 'btn');
  call.append(icon('phone'), document.createTextNode('Chiama'));
  if (venue.telefono) {
    call.href = `tel:${venue.telefono.replace(/[^+\d]/g, '')}`;
  } else {
    call.type = 'button';
    call.disabled = true;
  }
  actions.append(call);

  /*
   * The one action that always works.
   *
   * Two of the three buttons on this row are off for most venues — no
   * confirmed address, no phone number — so the card opened with a row of
   * grey. This one needs nothing from the catalogue: the address of the card
   * itself, which has existed since the app learned to answer `/locale/...`,
   * and which is how anything here reaches anybody.
   *
   * Copying, not sharing: `navigator.share` was taken out with the giro and
   * is not worth bringing back for this.
   */
  const copy = el('button', 'btn');
  copy.type = 'button';
  const copyLabel = el('span', null, 'Copia link');
  copy.append(icon('link'), copyLabel);
  copy.addEventListener('click', async () => {
    const address = `${window.location.origin}/locale/${venue.id}`;
    copyLabel.textContent = (await copyToClipboard(address))
      ? 'Copiato'
      : 'Copia non riuscita';
    setTimeout(() => {
      copyLabel.textContent = 'Copia link';
    }, 1600);
  });
  actions.append(copy);

  wrapper.append(actions);

  // No note under a button that is off. There used to be one of three, each
  // explaining our verification process; the maintainer asked for them gone,
  // and the grey button already says it cannot be used.

  return wrapper;
}

/**
 * Come e' andata la puntata a questo locale, detto solo se lo sappiamo.
 *
 * "Esito: non confermato" era ambiguo due volte. Primo, in una scheda che
 * parla di indirizzi da confermare e stati da verificare, quella riga si
 * leggeva come "qualcosa non torna con questo locale", mentre parla della
 * puntata. Secondo, non distingueva fra "non ha vinto" e "non sappiamo come
 * sia andata", che sono cose diverse: di un locale di Foodish sappiamo
 * benissimo che non ha vinto, perche' il vincitore di quella puntata e' un
 * altro e ce l'abbiamo in catalogo.
 *
 * La distinzione si regge su una prova, non su un'assenza: si dice "non ha
 * vinto" soltanto se fra i locali della stessa puntata ce n'e' uno marcato
 * vincitore. Se in catalogo quella puntata e' incompleta e il vincitore non
 * si vede, resta "non noto" — perche' per quanto ne sappiamo potrebbe essere
 * proprio questo.
 */
/**
 * Gli altri locali della stessa puntata.
 *
 * Programma, data e titolo: lo stesso test che il piazzamento usa per decidere
 * se puo' dire "non ha vinto". Sta scritto una volta sola apposta — due copie
 * della stessa regola possono divergere, e allora la scheda direbbe "non ha
 * vinto" senza mostrare chi ha vinto, o viceversa.
 */
function altriDellaPuntata(venue, venues) {
  return (venues ?? []).filter(
    (altro) =>
      altro.id !== venue.id &&
      altro.programma === venue.programma &&
      altro.puntata?.data === venue.puntata?.data &&
      altro.puntata?.titolo === venue.puntata?.titolo
  );
}

function piazzamento(venue, venues) {
  const detto = outcomeLabel(venue);
  if (detto) return detto;

  const vincitore = altriDellaPuntata(venue, venues).some(
    (altro) => altro.vincitore === true
  );

  return vincitore ? 'Non ha vinto' : null;
}

/**
 * The venues this one was up against.
 *
 * These programmes put places in competition with each other, so the obvious
 * next question after reading one card is who else was in that episode — and
 * the answer was already in the catalogue, reachable only by going back and
 * hunting for it. Rendered as the same card used everywhere else, so what is
 * clearly a venue keeps looking like one.
 *
 * Nothing is drawn when the episode holds nobody else: an empty "others"
 * heading would suggest the catalogue is missing them, when usually there
 * simply were no others.
 */
function altriBlock(venue, ctx) {
  const altri = altriDellaPuntata(venue, ctx.venues);
  if (altri.length === 0) return null;

  const block = el('div', 'block');
  // An h2, like the other blocks holding cards: their names are h3, and the
  // outline would otherwise jump from the venue's h1 straight to them.
  block.append(
    el(
      'h2',
      'block__label',
      altri.length === 1 ? 'L’altro della puntata' : 'Gli altri della puntata'
    )
  );

  // Winner first: it is the one piece of the episode a reader is looking for.
  const ordinati = [...altri].sort((a, b) => {
    if (Boolean(a.vincitore) !== Boolean(b.vincitore)) return a.vincitore ? -1 : 1;
    return (a.nome ?? a.id).localeCompare(b.nome ?? b.id, 'it');
  });

  const list = el('div', 'altri');
  for (const altro of ordinati) list.append(createCard(altro, ctx));
  block.append(list);

  return block;
}

/**
 * The same place, seen in another programme.
 *
 * A restaurant that went through 4 Ristoranti and, years later, Foodish is one
 * row per appearance: each has its own episode, rival and outcome. What ties
 * them is the Google Maps listing — the same `google_id`, which only a lookup
 * that decided, or a person, ever writes. Same address is not enough: two
 * businesses share Via Mazzini 20 in Milan. The Worker draws the same block
 * (`twins` in `worker/pagine.js`).
 */
function twinsBlock(venue, ctx) {
  if (!venue.google_id) return null;
  const twins = (ctx.venues ?? []).filter(
    (other) => other.id !== venue.id && other.google_id === venue.google_id && !isGuide(other)
  );
  if (twins.length === 0) return null;

  const block = el('div', 'block');
  block.append(
    el(
      'h2',
      'block__label',
      twins.length === 1
        ? 'Lo stesso locale, in un’altra puntata'
        : 'Lo stesso locale, in altre puntate'
    )
  );
  const list = el('div', 'altri');
  for (const twin of twins) list.append(createCard(twin, ctx));
  block.append(list);
  return block;
}

/** How many venues under a card stand for the rest of its zone. */
const NEIGHBOURS = 8;

const OPEN_FIRST = { aperto: 0 };

/**
 * Other venues in the same zone, and the way to all of them.
 *
 * The episode's own are left out, being already listed above. Open ones
 * first, then the most recent: this is a suggestion of where else to go. The
 * Worker writes the same list into the page (`worker/pagine.js`), so what a
 * search engine reads is what a visitor sees.
 */
function neighboursBlock(venue, ctx) {
  if (!venue.zona) return null;
  const taken = new Set(altriDellaPuntata(venue, ctx.venues).map((altro) => altro.id));
  if (venue.google_id) {
    for (const other of ctx.venues ?? []) {
      if (other.google_id === venue.google_id) taken.add(other.id);
    }
  }
  const sameZone = (ctx.venues ?? []).filter(
    (other) => other.zona === venue.zona && other.id !== venue.id && !isGuide(other)
  );
  const shown = sameZone
    .filter((other) => !taken.has(other.id))
    .sort(
      (a, b) =>
        (OPEN_FIRST[a.stato] ?? 1) - (OPEN_FIRST[b.stato] ?? 1) ||
        (b.puntata?.data ?? '').localeCompare(a.puntata?.data ?? '') ||
        (a.nome ?? '').localeCompare(b.nome ?? '', 'it')
    )
    .slice(0, NEIGHBOURS);
  if (shown.length === 0) return null;

  const block = el('div', 'block');
  block.append(el('h2', 'block__label', `Altri locali visti in TV · ${venue.zona}`));
  const list = el('div', 'altri');
  for (const other of shown) list.append(createCard(other, ctx));
  block.append(list);

  const slug = zoneSlug(venue.zona);
  const zone = ctx.zonesBySlug?.get(slug);
  if (zone && ctx.onOpenZone) {
    const all = el('button', 'btn btn--wide btn--spaced', `${allOf(zone.n)} locali della zona`);
    all.type = 'button';
    all.addEventListener('click', () => ctx.onOpenZone(slug));
    block.append(all);
  }
  return block;
}

function episodeBlock(venue, { programNames, venues, episodes, onOpenEpisode }) {
  const block = el('div', 'block');
  block.append(el('p', 'block__label', 'Puntata'));

  const list = el('dl', 'kv-list');
  list.append(
    keyValue('Programma', programNames.get(venue.programma) ?? venue.programma)
  );
  // A row whose only content would be "we do not know" is left out.
  if (venue.puntata?.titolo) list.append(keyValue('Puntata', venue.puntata.titolo));
  const aired = formatEpisodeDate(venue.puntata);
  if (aired) list.append(keyValue('Andata in onda', aired));
  const placing = piazzamento(venue, venues);
  if (placing) list.append(keyValue('Piazzamento', placing));
  block.append(list);

  // The whole episode on a page of its own, where it has one (`src/puntate.js`).
  const page = episodes?.byKey.get(venueEpisodeKey(venue));
  if (page && onOpenEpisode) {
    const all = el('button', 'btn btn--wide btn--spaced', 'Vedi la puntata →');
    all.type = 'button';
    all.addEventListener('click', () => onOpenEpisode(page.slug));
    block.append(all);
  }

  return block;
}

function todayBlock(venue, { onOpenReport }) {
  // Right under the buttons, since it is this venue's own: where it is, how
  // to call it, and whether it is still open.
  const block = el('div', 'block');
  block.append(el('p', 'block__label', 'Il locale oggi'));

  const list = el('dl', 'kv-list kv-list--spaced');
  const status = el('div', 'kv');
  status.append(el('dt', null, 'Stato'));
  status.append(
    el('dd', `state state--${venue.stato}`, STATUS_LABELS[venue.stato])
  );
  list.append(status);

  // The status is only worth as much as the date on it. Only the date: who
  // or what checked it meant something to us and nothing to a visitor.
  const checked = formatDate(venue.verificato_il);
  if (checked) list.append(keyValue('Verificato', checked));

  // Only a real street. A zone is a zone, and calling it an address is how
  // somebody ends up in the wrong place; the zone is already under the name.
  if (venue.indirizzo) list.append(keyValue('Indirizzo', venue.indirizzo));

  if (venue.telefono) list.append(keyValue('Telefono', venue.telefono));
  block.append(list);

  const report = el('button', 'btn btn--wide btn--spaced', 'Segnala una correzione');
  report.type = 'button';
  report.addEventListener('click', () => onOpenReport(venue.id));
  block.append(report);

  return block;
}

/**
 * What the guide says about a starred restaurant, in place of the episode:
 * its stars, its kitchen, the chef people know from television, and where
 * the list comes from. The Worker draws the same (`guideVenueBody`).
 */
function guideBlock(venue) {
  const block = el('div', 'block');
  block.append(el('p', 'block__label', `Guida Michelin ${GUIDE_YEAR}`));
  const list = el('dl', 'kv-list');
  list.append(keyValue('Stelle', `${starsGlyph(venue.stelle)} ${starsWords(venue.stelle)}`));
  if (venue.cucina) list.append(keyValue('Cucina', venue.cucina));
  const chef = tvChef(venue);
  if (chef) list.append(keyValue('Chef visto in TV', chef));
  block.append(list);
  block.append(el('p', 'note', GUIDE_CREDIT));
  return block;
}

/** The town's other starred restaurants, and the way to its page. */
function guideTownBlock(venue, ctx) {
  if (!venue.zona) return null;
  const slug = guideCitySlug(venue.zona);
  const others = (ctx.venues ?? [])
    .filter((other) => isGuide(other) && other.zona === venue.zona && other.id !== venue.id)
    .sort(byStars)
    .slice(0, NEIGHBOURS);

  const block = el('div', 'block');
  block.append(el('h2', 'block__label', `Stellati · ${venue.zona}`));
  if (others.length) {
    const list = el('div', 'altri');
    for (const other of others) list.append(createCard(other, ctx));
    block.append(list);
  }
  if (ctx.onOpenGuideCity) {
    const all = el('button', 'btn btn--wide btn--spaced', guideCityTitle(venue.zona));
    all.type = 'button';
    all.addEventListener('click', () => ctx.onOpenGuideCity(slug));
    block.append(all);
  }
  return block;
}

export function renderDetail(container, venue, ctx) {
  if (isGuide(venue)) {
    container.replaceChildren(
      ...[
        heroBlock(venue, ctx),
        actionsBlock(venue),
        guideBlock(venue),
        todayBlock(venue, ctx),
        guideTownBlock(venue, ctx),
      ].filter(Boolean)
    );
    container.lastElementChild?.classList.add('block--last');
    container.scrollTop = 0;
    return;
  }
  container.replaceChildren(
    ...[
      // Only a real photograph earns the top of the card: an empty box there
      // pushed the name a third of the way down the screen for nothing.
      venue.foto && venuePhoto(venue, 'hero'),
      heroBlock(venue, ctx),
      actionsBlock(venue),
      // The place itself before the places around it: under the other venues'
      // cards, the address and the phone were three screens down on a phone.
      todayBlock(venue, ctx),
      episodeBlock(venue, ctx),
      altriBlock(venue, ctx),
      twinsBlock(venue, ctx),
      neighboursBlock(venue, ctx),
    ].filter(Boolean)
  );
  // Whichever block ends the card keeps the room the bottom bar covers.
  container.lastElementChild?.classList.add('block--last');
  container.scrollTop = 0;
}

/** Subtitle of the programme screen: how much of it we hold, and how many wins. */
export function programSummary(venues) {
  const wins = venues.filter((venue) => venue.vincitore).length;
  const venuePart = `${venues.length} ${venues.length === 1 ? 'locale' : 'locali'} in catalogo`;
  if (!wins) return venuePart;
  return `${venuePart} · ${wins} ${wins === 1 ? 'vincitore' : 'vincitori'} di puntata`;
}
